/**
 * Use-case landing page content.
 * Each entry maps to /use-cases/:slug
 */

export const useCases = {
  teachers: {
    slug: 'teachers',
    title: 'Spin the Names for Teachers',
    headline: 'The Fairest Way to Pick a Student',
    subheadline:
      'Boost participation, reduce anxiety, and make every lesson more engaging with a random name picker your whole class will trust.',
    keywords: ['random name picker classroom', 'student picker wheel', 'teacher tools', 'classroom engagement'],
    description:
      'Discover how teachers use Spin the Names to boost classroom participation, assign jobs fairly, and keep every student engaged.',
    heroIcon: 'fa-chalkboard-teacher',
    problem: {
      heading: 'The Same Hands Go Up Every Time',
      body: `Every teacher knows the feeling: you ask a question, and the same five students raise their hands. The rest of the class checks out, knowing they probably won't be called. It's not fair — and it's not great for learning.

Random selection changes the dynamic. When any student might be picked at any moment, everyone stays engaged. And when the selection comes from a spinning wheel instead of a teacher's judgment, students accept it as genuinely fair.`,
    },
    steps: [
      {
        icon: 'fa-users',
        heading: 'Add Your Class Roster',
        body: 'Type your students\' names into the wheel, or paste them in all at once. Takes under a minute.',
      },
      {
        icon: 'fa-save',
        heading: 'Save Your Wheel',
        body: 'Create a free account and save your class wheel. It\'ll be waiting for you every day — no re-entering names.',
      },
      {
        icon: 'fa-sync',
        heading: 'Spin for Any Occasion',
        body: 'Call on students, assign jobs, pick reading order, form groups — use it whenever you need a fair random pick.',
      },
    ],
    usages: [
      {
        icon: 'fa-hand-point-up',
        title: 'Cold-Call Without the Dread',
        body: 'Spin instead of picking favorites. Frame it as a chance to shine, not a gotcha, and students will embrace it.',
      },
      {
        icon: 'fa-broom',
        title: 'Classroom Jobs',
        body: 'Who waters the plants? Who leads morning meeting? Spin for it. Eliminates arguments and keeps rotation fair.',
      },
      {
        icon: 'fa-users',
        title: 'Random Group Formation',
        body: 'Break students out of their usual friend clusters. Random groups build social skills and fresh perspectives.',
      },
      {
        icon: 'fa-book-open',
        title: 'Reading Order',
        body: 'Keep everyone following along by randomizing who reads next. No more zoning out until it\'s almost their turn.',
      },
    ],
    tip: 'Let students see the wheel spin on a projector. The anticipation and excitement keeps energy high and makes even routine activities feel like a game.',
    cta: {
      heading: 'Ready for Your Classroom?',
      body: 'Build your class roster wheel in under a minute. Free to use, no download required.',
    },
  },

  events: {
    slug: 'events',
    title: 'Spin the Names for Events & Giveaways',
    headline: 'Run a Giveaway Everyone Trusts',
    subheadline:
      'Pick raffle winners, prize recipients, and game participants with a transparent, exciting random wheel your audience can see.',
    keywords: ['raffle wheel', 'prize wheel spinner', 'giveaway picker', 'online raffle tool'],
    description:
      'Run fair, exciting giveaways and raffles at events with Spin the Names. A transparent random wheel your audience can see and trust.',
    heroIcon: 'fa-gift',
    problem: {
      heading: 'Nobody Trusts "I Picked Randomly"',
      body: `When you announce a giveaway winner and just say "I picked randomly," people wonder. Was it actually random? Did someone get special treatment? The moment fairness is questioned, the goodwill from your giveaway evaporates.

A visible spinning wheel changes everything. The audience watches the wheel land in real time. There's no room for doubt — the outcome is transparent, exciting, and indisputably fair.`,
    },
    steps: [
      {
        icon: 'fa-list',
        heading: 'Add Your Participants',
        body: 'Enter names, ticket numbers, or usernames. Paste a whole list at once from a spreadsheet or sign-up form.',
      },
      {
        icon: 'fa-eye',
        heading: 'Show the Spin Live',
        body: 'Share your screen or project the wheel. Let your audience watch the selection happen in real time.',
      },
      {
        icon: 'fa-trophy',
        heading: 'Pick and Celebrate',
        body: 'Spin to reveal the winner. Remove them from the wheel and spin again for multiple prizes.',
      },
    ],
    usages: [
      {
        icon: 'fa-ticket-alt',
        title: 'Raffle Drawings',
        body: 'Add all ticket holders to the wheel and spin live at your event. Everyone sees it happen — no disputes.',
      },
      {
        icon: 'fa-star',
        title: 'Prize Giveaways',
        body: 'Perfect for trade shows, conferences, and community events. Spin for each prize tier separately.',
      },
      {
        icon: 'fa-gamepad',
        title: 'Game & Activity Selection',
        body: 'Which team goes first? Who gets the bonus round? Let the wheel decide and keep the energy up.',
      },
      {
        icon: 'fa-microphone',
        title: 'Audience Participation',
        body: 'Pick who gets to come on stage, answer a trivia question, or win a spot in a demo. Fair and thrilling.',
      },
    ],
    tip: 'For high-stakes giveaways, remove each winner from the wheel before spinning again so no one can win twice.',
    cta: {
      heading: 'Make Your Next Giveaway Unforgettable',
      body: 'Free to use, no sign-up required. Add your list and start spinning in seconds.',
    },
  },

  streamers: {
    slug: 'streamers',
    title: 'Spin the Names for Streamers',
    headline: 'Reward Your Community on Stream',
    subheadline:
      'Pick giveaway winners, choose challengers, and create unpredictable moments your chat will lose their minds over.',
    keywords: ['twitch giveaway wheel', 'stream picker', 'streamer tools', 'twitch giveaway', 'stream giveaway picker'],
    description:
      'Pick giveaway winners and create exciting random moments for your stream. Spin the Names is the fairest way to reward your community live.',
    heroIcon: 'fa-broadcast-tower',
    problem: {
      heading: 'Chat Wants to Know It\'s Fair',
      body: `Your community shows up, participates, and hopes to win — but if they can\'t see how the winner is chosen, suspicion creeps in. Accusations of favoritism can sour your chat and damage the trust you've built.

A live spinning wheel fixes this instantly. Chat watches every name fly by. They see exactly how the winner is chosen. The excitement of the spin becomes content in itself — and the fairness is undeniable.`,
    },
    steps: [
      {
        icon: 'fa-comment',
        heading: 'Collect Entries',
        body: 'Have viewers type a keyword in chat, use a sign-up form, or add subscribers and followers manually.',
      },
      {
        icon: 'fa-desktop',
        heading: 'Add to Wheel & Share Screen',
        body: 'Paste names into the wheel and share your screen. Chat watches every spin live.',
      },
      {
        icon: 'fa-trophy',
        heading: 'Spin and Announce',
        body: 'Let the wheel pick the winner. The moment of landing is pure hype — chat goes wild.',
      },
    ],
    usages: [
      {
        icon: 'fa-gift',
        title: 'Subscriber Giveaways',
        body: 'Reward your subs with a live wheel spin. Everyone in the draw can see their name on screen.',
      },
      {
        icon: 'fa-gamepad',
        title: 'Viewer Challenges',
        body: 'Spin to pick who gets to 1v1 you, join your squad, or suggest the next challenge.',
      },
      {
        icon: 'fa-question-circle',
        title: 'Random Game Decisions',
        body: 'Let chat vote on options, then spin to choose. Great for choose-your-own-adventure style content.',
      },
      {
        icon: 'fa-users',
        title: 'Community Events',
        body: 'Tournament brackets, team selections, turn order — the wheel handles it all transparently.',
      },
    ],
    tip: 'Save your regular viewer wheel between streams so returning community members stay in the pool. Reward loyalty without extra admin.',
    cta: {
      heading: 'Give Your Chat a Moment to Hype',
      body: 'Free, instant, no install. Add your viewer list and spin live on stream.',
    },
  },

  business: {
    slug: 'business',
    title: 'Spin the Names for Teams & Business',
    headline: 'Remove the Politics from Random Decisions',
    subheadline:
      'Pick meeting participants, assign tasks, form teams, and make fair random choices your whole team can see and trust.',
    keywords: ['random team picker', 'meeting name selector', 'team building tools', 'random task assignment'],
    description:
      'Make fair, transparent random decisions at work. Spin the Names removes bias from team assignments, task allocation, and meeting participation.',
    heroIcon: 'fa-briefcase',
    problem: {
      heading: 'Someone Always Thinks It\'s Rigged',
      body: `"Why does Sarah always get the good assignments?" "How come the same people present every week?" When random decisions are made invisibly, people fill in the blanks with suspicion — even when there's no bias at all.

A transparent random wheel eliminates this. Everyone sees the selection happen. There's no favoritism to question, no explanation needed. The wheel decided, and that's that.`,
    },
    steps: [
      {
        icon: 'fa-users',
        heading: 'Add Your Team',
        body: 'Enter team member names. Save the wheel to reuse in every meeting or standup.',
      },
      {
        icon: 'fa-share-alt',
        heading: 'Share the Screen',
        body: 'Show the spin on a video call or in-room display. Everyone watches the same wheel.',
      },
      {
        icon: 'fa-check-circle',
        heading: 'Spin and Move On',
        body: 'Decision made in seconds. No debate, no hurt feelings, no follow-up questions about fairness.',
      },
    ],
    usages: [
      {
        icon: 'fa-microphone-alt',
        title: 'Meeting Kick-Offs',
        body: 'Spin to pick who gives the first update. Keeps standups moving and everyone on their toes.',
      },
      {
        icon: 'fa-tasks',
        title: 'Task Assignment',
        body: 'For equivalent tasks no one wants to own, let the wheel decide. Nobody can argue with random.',
      },
      {
        icon: 'fa-object-group',
        title: 'Team Formation',
        body: 'Random project teams avoid the usual cliques and give everyone a chance to work with new people.',
      },
      {
        icon: 'fa-ice-cream',
        title: 'Team Perks & Fun',
        body: 'Who chooses the team lunch spot? Who gets the parking spot this week? Keep it light and fair.',
      },
    ],
    tip: 'For recurring decisions, save named wheels per team or project. You\'ll have them ready in seconds for every meeting.',
    cta: {
      heading: 'Make Your Next Team Decision Effortless',
      body: 'Free to use, works in any browser. No install, no signup required.',
    },
  },
};

export function getUseCase(slug) {
  return useCases[slug] || null;
}

export function getAllUseCaseSlugs() {
  return Object.keys(useCases);
}
